import ApiLoader from '@amap/amap-jsapi-loader'

const buffer = new Map()

/**
 * 高德地图js-api加载函数
 * @param fn js-api加载完成后的回调函数
 */
export function loadAmap(fn) {
  return new Promise((resolve) => {
    if (!window.AMap) {
      ApiLoader.load({
        key: '34aaa451ceb3f2224d77abeffc04285f',
        version: '2.0', // 加载的JSAPI版本，缺省为 1.4.15
        // Loca: { version: '2.0.0' }, // Loca版本,缺省为1.3.2
        // 加载地图插件,插件列表参考//*https://lbs.amap.com/api/javascript-api-v2/guide/abc/plugins-list
        plugins: ['AMap.Geocoder','AMap.AutoComplete','AMap.Scale']
      }).then((AMap) => {
        fn && fn(AMap)
        resolve(AMap)
      }).catch(error => {
        resolve(undefined)
        if (error && error.message.indexOf('container div not exist') > -1) {
          console.log('%c 地图容器不存在', 'background:#999;color:#FFFF00;padding:3px 6px 1px 0')
        } else {
          console.warn('js api load fail:', error)
        }
      })
    } else {
      fn && fn(AMap)
      resolve(AMap)
    }
  })
}

/**
 * 高德地图地理编码：地址信息 => 地理坐标(经纬度)
 */
export async function geoLocation(name) {
  if (!AMap || !AMap.Geocoder) {
    console.info('需先加载地图JS-API或Geocoder插件')
    await loadAmap()
  }
  let city = '全国'
  if (name.indexOf('市') > -1) {
    const sct = name.split('市')[0]
    city = sct.indexOf('省') > -1 ? sct.split('省')[1] || sct : sct
  }
  const mapGeo = new AMap.Geocoder({ city, lang: 'zh_cn' })
  return new Promise((resolve) => {
    mapGeo.getLocation(name, (status, res) => {
      if (status === 'complete' && res.info === 'OK') {
        resolve(res.geocodes[0])
      } else {
        console.warn(`地址${name}没有解析到结果!`)
        resolve(undefined)
      }
    })
  })
}

/**
 * 搜索高德地图POI
 * @param keyword 搜索关键字
 * @param options 搜索提示配置参数 { city: 输入提示时限定城市,缺省为“全国”, citylimit: 是否限制在设置的城市内搜索,缺省为false }
 * @tip 相关服务配额说明
 * @service 输入提示服务
 ** QPD(单日调用量上限) 5000万 次/日
 ** QPS(并发量上限) 200 次/秒
 * @service 搜索服务-根据关键字查询行政区或商圈信息
 ** QPD(单日调用量上限) 5000万 次/日
 ** QPS(并发量上限) 500 次/秒
 */
export async function searchMapPoi(keyword, options) {
  if (!AMap || !AMap.AutoComplete) {
    console.info('需先加载地图JS-API或Autocomplte插件')
    await loadAmap()
  }
  if (!keyword) {
    console.warn('POI搜索关键字不能为空')
    return
  }
  const autoInstance = new AMap.AutoComplete(options)
  return new Promise((resolve) => {
    const kw = keyword.trim()
    autoInstance.search(kw, (status, res) => {
      console.log('search POI:', status, res)
      if (status === 'complete' && res.info === 'OK') {
        // const filt = res.tips.filter((p) => p && p.name && p.name.indexOf(kw) > -1)
        const filt = res.tips.filter(p => p.id && p.location) // 过滤无效的POI搜索结果
        resolve(filt)
      } else {
        resolve(undefined)
      }
    })
  })
}

/**
 * 将其他坐标系的坐标点转换为高德坐标系(火星坐标系GCJ-02)
 * @param {AMap.LngLat|AMap.LngLat[]} lnglats 坐标点,最多支持40对坐标
 * @param {String} type 坐标类型
 * @tip type可选值为 gps: GPS原始坐标(地球坐标系), baidu: 百度坐标, mapbar: 图吧坐标, gcj: 高德坐标(传此值坐标会原样返回,即不做转换)
 * @tip 服务配额说明
 * @service AMap.convertFrom 坐标转换服务
 ** QPD(单日调用量上限) 5000万 次/日
 ** QPS(并发量上限) 300 次/秒
 * @returns 转换成功返回高德坐标点集合，转换失败返回undefined
 */
export async function convert2Gcj(lnglats, type) {
  if (!AMap) {
    await loadAmap()
  }
  const k = `${type}_${JSON.stringify(lnglats)}`
  return new Promise((resolve) => {
    AMap.convertFrom(lnglats, type, function (status, result) {
      // 异常处理(并发量大时小概率出现转换失败的情况),200ms后自动重试一次
      if (status === 'error' || result === 'Request Error') {
        const count = buffer.get(k)
        if (!count) {
          buffer.set(k, 1)
          const t = setTimeout(() => {
            clearTimeout(t)
            convert2Gcj(lnglats, type)
          }, 200)
        } else {
          buffer.delete(k)
          console.warn(`坐标转换失败${lnglats}，${type}`)
          resolve()
        }
      } else if (result.info === 'ok') {
        resolve(result.locations)
      }
    })
  })
}

/**
 * 地理坐标转换工具类
 */
export const GeoUtil = {
  S_PI: 3.14159265358979324 * 3000.0 / 180.0,
  /**
  * 高德 => 百度
  * @param {Object} p 高德坐标系下的坐标点
  */
  gcj02ToBdPoint(p) {
    return this.gcj02ToBd09(p.lng, p.lat)
  },

  /**
   * 高德坐标经纬度转换为百度坐标经纬度
   * @param longitude 火星坐标系下的经度(高德坐标)
   * @param latitude 火星坐标系下的纬度(高德坐标)
   * @returns 百度坐标系下的经纬度对象 {lng:Number,lat:Number}
   */
  gcj02ToBd09(longitude, latitude) {
    if (isNaN(longitude) || isNaN(latitude)) {
      console.warn('经度、纬度必须是数字或数字字符串！', longitude, latitude)
      return
    }
    const x = longitude
    const y = latitude
    const z = Math.sqrt(x * x + y * y) + 0.00002 * Math.sin(y * this.S_PI)
    const theta = Math.atan2(y, x) + 0.000003 * Math.cos(x * this.S_PI)
    const lng = Number((z * Math.cos(theta) + 0.0065).toFixed(6))
    const lat = Number((z * Math.sin(theta) + 0.006).toFixed(6))
    return { lng, lat }
  },

  /**
   * 百度 => 高德
   * @param {Object} p 百度坐标系下的坐标点
   */
  bd09ToGdPoint(p) {
    return this.bd09ToGcj02(p.lng, p.lat)
  },

  /**
   * 百度坐标经纬度转换为高德坐标经纬度
   * @param longitude 百度坐标系下的经度
   * @param latitude 百度坐标系下的纬度
   * @returns 高德坐标系下的经纬度对象 {lng:Number,lat:Number}
   */
  bd09ToGcj02(longitude, latitude) {
    if (isNaN(longitude) || isNaN(latitude)) {
      throw new Error(`经度、纬度必须是数字或数字字符串！${longitude}、${latitude}`);
    }
    const x = Number(longitude - 0.0065)
    const y = Number(latitude - 0.006)
    const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * this.S_PI)
    const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * this.S_PI)
    const lng = Number((z * Math.cos(theta)).toFixed(6))
    const lat = Number((z * Math.sin(theta)).toFixed(6))
    return { lng, lat }
  }
}



export const RR = {
  random() {
    // 生成 32 位随机值
    const f32Arr = new Uint32Array(1)
    // 最大值是 2^32 –1
    const maxUint32 = 0xFFFFFFFF
    // 用最大可能的值来除
    return crypto.getRandomValues(f32Arr)[0] / maxUint32
  },
  randomNum(min = 0, max = 1) {
    return Math.random() * (max - min) + min
  }
}

window.mockNbhd = function(init, count = 100) {
  const { lon, lat, ...other } = init
  return Array(count).fill(1).map((_, n) => {
    const dl = RR.random() > 0.3 ? -1 : 1
    const da = RR.random() > 0.6 ? -1 : 1
    return {
      ...other,
      guid: 12693 + n,
      lon: lon + dl * RR.random() * RR.random(),
      lat: lat + da * RR.random() * RR.random()
    }
  })
}
